import { isAnonymousChatAtom } from '@/atoms/chatLaunchAtoms';
import { sidebarChatIdAtomFamily } from '@/atoms/chatSidebarAtoms';
import { createChat } from '@/pages/api/scribeAPIV2';
import { type ChatListItem, type CreateChatRequest } from '@/types';
import {
  BACKEND_PLACEHOLDER_CHAT_TITLE,
  isBackendPlaceholderChatTitle,
} from '@/utils/chatPlaceholderTitle';
import { setCurrentChatId } from '@/utils/currentChatId';
import { logger } from '@/utils/logger/logger';
import { setTempUserId } from '@/utils/tempUser';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { ChatListItemVersion, ChatMode as SchemaChatMode } from '@heidi/schema/ml-scribe';
import { type ChatListResponse } from '@heidi/session/contracts';
import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { getDefaultStore, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { getInfiniteChatsQueryKey } from '../query/useInfiniteQueryChats';
import { GET_CHATS } from '../query/useQueryChats';
import { GET_SESSION_CHATS } from '../query/useQuerySessionChats';

/**
 * Input for createChat. `inSessionSidebar` (EVI-1680) marks a chat that is being
 * created for the in-session sidebar experience rather than the standalone chat
 * route. When set, onSuccess selects the chat in the session sidebar (via
 * `sidebarChatIdAtomFamily`) instead of navigating to `/scribe/chat/[id]`, so
 * the user stays in their session view. Used by the eager-create path that lets
 * Evidence SBS setup fire on the very first in-session message.
 */
type CreateChatInput = CreateChatRequest & {
  inSessionSidebar?: boolean;
  /** Hosted chat (inbox document sheet). Create the chat, but stay on this page. */
  stayOnPage?: boolean;
};

/**
 * Optimistic sidebar title on chat create. Stays on the placeholder until the
 * backend returns a real title or the async title LLM updates the row.
 */
export const deriveOptimisticChatTitle = (
  responseTitle: string | null | undefined
): string => {
  const serverTitle = responseTitle?.trim();
  if (serverTitle && !isBackendPlaceholderChatTitle(serverTitle)) {
    return serverTitle;
  }
  return BACKEND_PLACEHOLDER_CHAT_TITLE;
};

const buildOptimisticChatListItem = ({
  chatId,
  title,
  firstMessage,
  sessionId,
  patientProfileId,
}: {
  chatId: string;
  title: string;
  firstMessage?: string | null;
  sessionId?: string | null;
  patientProfileId?: string | null;
}): ChatListItem => {
  const now = new Date().toISOString();
  // Cast: generated OpenAPI still requires retired ``has_schedule`` until
  // schema regen after the BE hard-cut lands. Do not write the field.
  return {
    id: chatId,
    title,
    session_id: sessionId ?? null,
    patient_profile_id: patientProfileId ?? null,
    mode: SchemaChatMode.evidence,
    is_saved: false,
    created_at: now,
    updated_at: now,
    preview: firstMessage?.slice(0, 100) ?? null,
    // Sidebar/getChatHref: v1 → /scribe/chat/[id], v2 → /projects/[id].
    // This mutation only creates legacy Evidence chats.
    version: ChatListItemVersion.Value1,
  } as ChatListItem;
};

/** Insert the row, or retitle it when an earlier phase already inserted it. */
const upsertChatRow =
  (row: ChatListItem) =>
  (chats: ChatListItem[]): ChatListItem[] =>
    chats.some((chat) => chat.id === row.id)
      ? chats.map((chat) => (chat.id === row.id ? { ...chat, title: row.title } : chat))
      : [row, ...chats];

const withoutChatRow =
  (chatId: string) =>
  (chats: ChatListItem[]): ChatListItem[] =>
    chats.filter((chat) => chat.id !== chatId);

/**
 * Apply a transform to both sidebar caches (paginated page 1 + the infinite
 * query), moving every page's `total` by the row-count delta so `hasNextPage`
 * stays correct. Search queries are left to the `onSuccess` invalidation.
 */
const updateChatListCaches = (
  queryClient: QueryClient,
  transform: (chats: ChatListItem[]) => ChatListItem[]
): void => {
  queryClient.setQueryData<ChatListResponse>([GET_CHATS, 1, 100], (old) => {
    if (!old) return undefined;
    const chats = transform(old.chats);
    return { ...old, chats, total: old.total + (chats.length - old.chats.length) };
  });

  queryClient.setQueryData<InfiniteData<ChatListResponse, number>>(
    getInfiniteChatsQueryKey(), // Default: pageSize=20, query=null, hasSearch=false
    (old) => {
      const firstPage = old?.pages?.[0];
      if (!old || !firstPage) return old;
      const chats = transform(firstPage.chats);
      const delta = chats.length - firstPage.chats.length;
      return {
        ...old,
        pages: old.pages.map((page, index) => ({
          ...page,
          chats: index === 0 ? chats : page.chats,
          total: page.total + delta,
        })),
      };
    }
  );
};

export function useMutateCreateChat() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const setIsAnonymousChat = useSetAtom(isAnonymousChatAtom);
  const { t } = useTranslations();

  return useMutation({
    mutationFn: async ({
      inSessionSidebar = false,
      stayOnPage = false,
      ...input
    }: CreateChatInput) => {
      const data = await createChat(input);
      return {
        response: data,
        clientChatId: input.chatId,
        firstMessage: input.firstMessage,
        sessionId: input.sessionId,
        patientProfileId: input.patientProfileId,
        inSessionSidebar,
        stayOnPage,
      };
    },
    // Runs before mutationFn so the row lands the instant the user presses
    // enter instead of a round trip later. Needs the client chat id; without
    // one, onSuccess inserts instead.
    onMutate: ({ chatId, firstMessage, sessionId, patientProfileId }) => {
      if (!chatId) return undefined;

      updateChatListCaches(
        queryClient,
        upsertChatRow(
          buildOptimisticChatListItem({
            chatId,
            title: deriveOptimisticChatTitle(null),
            firstMessage,
            sessionId,
            patientProfileId,
          })
        )
      );

      return { insertedChatId: chatId };
    },
    onSuccess: ({
      response,
      clientChatId,
      firstMessage,
      sessionId,
      patientProfileId,
      inSessionSidebar,
      stayOnPage,
    }) => {
      // Extract and store temp_user_id from CreateChatResponse
      const tempUserId = response.data?.temp_user_id;

      // Use client-provided chatId if available, otherwise fall back to response
      const chatId = clientChatId ?? response.data?.chat_id;
      if (!chatId) {
        throw new Error('No chat ID available');
      }

      if (tempUserId) {
        setIsAnonymousChat(true);
        setTempUserId(tempUserId);
        setCurrentChatId(chatId);
      }

      if (inSessionSidebar && sessionId) {
        // EVI-1680: in-session sidebar chat — select it in the session view
        // instead of navigating to the standalone chat route. The chat's state
        // migration (session-${sessionId}-new → chat-${chatId}) and stream start
        // are handled by useSessionlessChatMigration on the resulting remount.
        getDefaultStore().set(sidebarChatIdAtomFamily(sessionId), chatId);
      } else if (!stayOnPage && tempUserId) {
        void router.push(`/chat/${chatId}`);
      } else if (!stayOnPage) {
        void router.push(`/scribe/chat/${chatId}`, undefined, { shallow: true });
      }

      // Retitles the row onMutate inserted, or inserts it when there was no
      // client chat id to insert under.
      updateChatListCaches(
        queryClient,
        upsertChatRow(
          buildOptimisticChatListItem({
            chatId,
            title: deriveOptimisticChatTitle(response.data?.title),
            firstMessage,
            sessionId,
            patientProfileId,
          })
        )
      );

      // If chat is linked to a session, invalidate session chats query
      if (sessionId) {
        void queryClient.invalidateQueries({
          queryKey: [GET_SESSION_CHATS, sessionId],
        });
      }

      // Invalidate other paginated queries so they refetch when accessed
      void queryClient.invalidateQueries({
        queryKey: [GET_CHATS],
        refetchType: 'none', // Don't refetch now, just mark stale
      });
    },
    onError: (error, _input, context) => {
      // The chat was never created, so drop the row onMutate inserted.
      if (context?.insertedChatId) {
        updateChatListCaches(queryClient, withoutChatRow(context.insertedChatId));
      }

      logger.error('Failed to create chat', { error });

      // Check if it's a rate limit error (429 status)
      const isRateLimitError =
        (error as { response?: { status?: number } })?.response?.status === 429 ||
        (error as { message?: string })?.message?.includes('429');

      if (isRateLimitError) {
        toast({
          title: t('chat.rateLimitError.toastTitle' as never),
          description: t('chat.rateLimitError.toastDescription' as never),
          tone: 'destructive',
        });
      }
    },
  });
}
